import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { LegalSection, LegalSubsection, OwnerBlock, PLATFORM_URL } from '../components/LegalPage';
import { BRAND } from '../config/brand';

const PrivacyPage = () => {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="Informativa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR) e del D.lgs. 196/2003 come modificato dal D.lgs. 101/2018"
    >
      <section className="space-y-2">
        <p>
          La presente Privacy Policy &egrave; resa da {BRAND.legalName} (di seguito anche
          &ldquo;{BRAND.companyName}&rdquo; o &ldquo;Titolare&rdquo;) agli utenti che si registrano e
          utilizzano {BRAND.productName}, la piattaforma digitale disponibile all&apos;indirizzo{' '}
          {PLATFORM_URL} (la &ldquo;Piattaforma&rdquo;), dedicata alla ricerca, consultazione e
          monitoraggio delle gare d&apos;appalto pubblicate su fonti pubbliche.
        </p>
        <p>
          La Policy descrive come il Titolare raccoglie e tratta i dati personali degli utenti durante
          la navigazione e l&apos;utilizzo dei servizi, inclusa la fase di registrazione ({PLATFORM_URL}/login).
        </p>
      </section>

      <LegalSection title="1. Titolare del trattamento e contatti">
        <p>Il Titolare del trattamento dei dati personali &egrave;:</p>
        <OwnerBlock />
        <p>
          {BRAND.companyName} opera dal 2005 nella consulenza strategica alle imprese nel settore degli
          appalti pubblici, nelle fasi di gara e di esecuzione delle commesse.
        </p>
        <p>
          Il Titolare non ha nominato un Responsabile della protezione dei dati (DPO), non ricorrendone
          i presupposti di cui all&apos;art. 37 GDPR.
        </p>
      </LegalSection>

      <LegalSection title="2. Ambito di applicazione">
        <p>La presente Privacy Policy si applica al trattamento dei dati personali degli utenti che:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>navigano la Piattaforma, anche senza registrazione, limitatamente ai dati di navigazione e agli strumenti tecnici descritti nella Cookie Policy;</li>
          <li>si registrano e creano un account;</li>
          <li>accedono alle aree riservate;</li>
          <li>utilizzano i servizi di ricerca, consultazione e monitoraggio delle gare;</li>
          <li>attivano notifiche e alert email personalizzati;</li>
          <li>si iscrivono alla Newsletter {BRAND.companyName}.</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Tipologie di dati trattati">
        <LegalSubsection title="3.1 Dati di navigazione e log di sicurezza" />
        <p>
          I sistemi informatici e le procedure software preposte al funzionamento della Piattaforma
          acquisiscono, nel corso del loro normale esercizio, alcuni dati personali la cui trasmissione
          &egrave; implicita nell&apos;uso dei protocolli di comunicazione Internet: a titolo
          esemplificativo indirizzo IP, tipo di browser e sistema operativo, data e ora della richiesta,
          risorsa richiesta, esito della richiesta, eventi di errore e log applicativi.
        </p>
        <p>
          L&apos;indirizzo IP &egrave; inoltre utilizzato, per brevi periodi, dai sistemi di limitazione
          delle richieste (rate limiting) che proteggono la Piattaforma da accessi abusivi e tentativi
          di forzatura delle credenziali.
        </p>

        <LegalSubsection title="3.2 Dati forniti dall'utente in fase di registrazione e nel profilo" />
        <p>In fase di registrazione e di gestione del profilo il Titolare tratta:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>nome e cognome;</li>
          <li>ragione sociale dell&apos;impresa o dello studio professionale;</li>
          <li>indirizzo email e numero di telefono cellulare;</li>
          <li>password, conservata esclusivamente in forma cifrata (hash) e mai leggibile dal Titolare;</li>
          <li>
            la risposta alla domanda sull&apos;associazione a {BRAND.companyName} (associato, non
            interessato, interessato a ricevere informazioni);
          </li>
          <li>l&apos;eventuale consenso alla Newsletter {BRAND.companyName};</li>
          <li>l&apos;accettazione dei Termini e Condizioni e la presa visione della presente Policy, con relativa data.</li>
        </ul>
        <p>
          I dati contrassegnati come obbligatori sono necessari per creare l&apos;account e fruire dei
          servizi: il mancato conferimento comporta l&apos;impossibilit&agrave; di registrarsi.
        </p>

        <LegalSubsection title="3.3 Dati relativi alle ricerche, alle notifiche e all'utilizzo del servizio" />
        <p>
          Durante l&apos;uso della Piattaforma sono trattati i criteri di ricerca e di notifica impostati
          dall&apos;utente (ad esempio codici CPV, categorie SOA, regioni e province, importi, parole
          chiave), la configurazione e la frequenza degli alert e lo storico delle notifiche email
          inviate, al fine di restituire risultati pertinenti e inviare alert coerenti.
        </p>

        <LegalSubsection title="3.4 Dati provenienti da fonti pubbliche" />
        <p>
          Le informazioni sulle gare sono acquisite da fonti pubbliche e ufficiali, in particolare dalla
          piattaforma Pubblicit&agrave; Legale dell&apos;Autorit&agrave; Nazionale Anticorruzione (ANAC).
          Tali avvisi possono contenere dati personali di soggetti diversi dagli utenti (ad esempio il
          nominativo del Responsabile unico del progetto o dei referenti della stazione appaltante),
          trattati esclusivamente per finalit&agrave; informative e di supporto alla ricerca delle gare,
          nei limiti in cui sono stati pubblicati dalla fonte e nel rispetto dei principi di
          pertinenza e minimizzazione.
        </p>
        <p>
          Tali dati non includono categorie particolari di dati personali ai sensi dell&apos;art. 9 GDPR,
          n&eacute; sono utilizzati per finalit&agrave; di valutazione, scoring o profilazione degli
          interessati.
        </p>
      </LegalSection>

      <LegalSection title="4. Finalità del trattamento e base giuridica">
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            <p className="font-semibold">Registrazione e gestione dell&apos;account</p>
            <p>
              Creazione dell&apos;account, verifica dell&apos;indirizzo email, autenticazione, recupero
              della password, gestione del profilo e del livello di accesso (Guest o Associato).
            </p>
            <p>Base giuridica: esecuzione di un contratto o di misure precontrattuali (art. 6, par. 1, lett. b GDPR).</p>
          </li>
          <li>
            <p className="font-semibold">Erogazione dei servizi di ricerca e consultazione delle gare</p>
            <p>
              Utilizzo delle funzionalit&agrave; di ricerca, filtro, consultazione e monitoraggio,
              compreso il periodo di prova gratuita riservato agli utenti Guest e la sua scadenza.
            </p>
            <p>Base giuridica: esecuzione del contratto (art. 6, par. 1, lett. b GDPR).</p>
          </li>
          <li>
            <p className="font-semibold">Notifiche e alert personalizzati</p>
            <p>
              Invio di email automatiche relative a gare di potenziale interesse, sulla base dei criteri
              impostati dall&apos;utente.
            </p>
            <p>Base giuridica: esecuzione del contratto (art. 6, par. 1, lett. b GDPR).</p>
            <p>
              Questa attivit&agrave; comporta una personalizzazione limitata, basata esclusivamente sui
              filtri scelti dall&apos;utente, senza effetti giuridici o analogamente significativi ai
              sensi dell&apos;art. 22 GDPR.
            </p>
          </li>
          <li>
            <p className="font-semibold">Gestione delle richieste di associazione e di informazioni</p>
            <p>
              Valutazione delle registrazioni da parte del personale del Titolare, abilitazione al
              livello Associato e, se l&apos;utente lo ha indicato in fase di registrazione, ricontatto
              per fornire informazioni sui servizi di {BRAND.companyName}.
            </p>
            <p>Base giuridica: misure precontrattuali adottate su richiesta dell&apos;interessato (art. 6, par. 1, lett. b GDPR).</p>
          </li>
          <li>
            <p className="font-semibold">Sicurezza, prevenzione degli abusi e gestione tecnica</p>
            <p>
              Protezione della Piattaforma e delle infrastrutture, prevenzione di accessi non
              autorizzati, frodi e abusi, gestione di malfunzionamenti e incidenti di sicurezza.
            </p>
            <p>
              Base giuridica: legittimo interesse del Titolare (art. 6, par. 1, lett. f GDPR) ed
              esecuzione del contratto (art. 6, par. 1, lett. b GDPR) per le attivit&agrave; necessarie
              all&apos;erogazione del servizio.
            </p>
          </li>
          <li>
            <p className="font-semibold">Newsletter {BRAND.companyName}</p>
            <p>Invio di comunicazioni informative sulle attivit&agrave;, sui servizi e sulle novit&agrave; di {BRAND.companyName}.</p>
            <p>
              Base giuridica: consenso (art. 6, par. 1, lett. a GDPR), facoltativo e revocabile in
              qualsiasi momento tramite il link di disiscrizione presente in ogni comunicazione o
              scrivendo al Titolare. Il mancato consenso o la sua revoca non pregiudicano l&apos;uso
              della Piattaforma. Le comunicazioni di servizio (conferma email, alert, avvisi
              sull&apos;account) sono inviate indipendentemente dal consenso alla newsletter e non hanno
              contenuto promozionale.
            </p>
          </li>
          <li>
            <p className="font-semibold">Adempimento di obblighi di legge e tutela dei diritti</p>
            <p>
              Adempimento di obblighi normativi, riscontro a richieste delle Autorit&agrave; competenti,
              accertamento, esercizio o difesa di un diritto.
            </p>
            <p>
              Base giuridica: obbligo legale (art. 6, par. 1, lett. c GDPR) e legittimo interesse del
              Titolare (art. 6, par. 1, lett. f GDPR).
            </p>
          </li>
        </ol>
      </LegalSection>

      <LegalSection title="5. Modalità del trattamento e misure di sicurezza">
        <p>
          Il trattamento &egrave; effettuato con strumenti informatici e telematici, secondo criteri di
          liceit&agrave;, correttezza e trasparenza e nel rispetto dei principi di minimizzazione,
          esattezza e limitazione della conservazione.
        </p>
        <p>
          Il Titolare adotta misure tecniche e organizzative adeguate al rischio, tra cui connessioni
          cifrate (HTTPS), password conservate solo in forma cifrata, controllo degli accessi basato su
          ruoli, limitazione delle richieste, backup, aggiornamenti periodici e procedure di gestione
          degli incidenti, nel rispetto del principio di responsabilizzazione (art. 5, par. 2, GDPR).
        </p>
      </LegalSection>

      <LegalSection title="6. Conferimento dei dati">
        <p>
          Il conferimento dei dati necessari alla registrazione e all&apos;erogazione dei servizi &egrave;
          obbligatorio: senza di essi non &egrave; possibile utilizzare la Piattaforma. Il consenso alla
          Newsletter &egrave; facoltativo.
        </p>
      </LegalSection>

      <LegalSection title="7. Destinatari dei dati">
        <p>I dati personali possono essere trattati da:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>personale e collaboratori del Titolare espressamente autorizzati e istruiti;</li>
          <li>
            fornitori di servizi tecnici nominati Responsabili del trattamento ai sensi dell&apos;art. 28
            GDPR, tra cui il fornitore dell&apos;infrastruttura cloud su cui &egrave; ospitata la
            Piattaforma (DigitalOcean, con server situati nell&apos;Unione europea, a Francoforte) e il
            fornitore del servizio di posta elettronica utilizzato per l&apos;invio delle email;
          </li>
          <li>soggetti pubblici o privati nei casi previsti dalla legge o su richiesta dell&apos;Autorit&agrave;.</li>
        </ul>
        <p>
          I dati personali non sono diffusi n&eacute; ceduti a terzi per finalit&agrave; di marketing.
          L&apos;elenco aggiornato dei Responsabili del trattamento pu&ograve; essere richiesto ai
          contatti indicati al punto 1.
        </p>
      </LegalSection>

      <LegalSection title="8. Trasferimento dei dati verso Paesi extra UE">
        <p>
          I dati della Piattaforma sono conservati su server situati nell&apos;Unione europea. Alcuni
          fornitori (ad esempio il fornitore dell&apos;infrastruttura cloud e Google, per il caricamento
          dei caratteri tipografici tramite Google Fonts) appartengono a gruppi con sede negli Stati
          Uniti: l&apos;eventuale trasferimento o accesso ai dati da Paesi extra UE avviene nel rispetto
          del Capo V del GDPR, sulla base della decisione di adeguatezza relativa al EU-U.S. Data
          Privacy Framework o di Clausole Contrattuali Standard.
        </p>
        <p>
          L&apos;interessato pu&ograve; ottenere informazioni sulle garanzie applicate contattando il
          Titolare ai recapiti indicati al punto 1.
        </p>
      </LegalSection>

      <LegalSection title="9. Periodo di conservazione">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            dati dell&apos;account, del profilo, dei criteri di ricerca e dello storico delle
            notifiche: per tutta la durata dell&apos;account. In caso di cancellazione su richiesta
            dell&apos;utente i dati sono eliminati senza ingiustificato ritardo; in caso di account
            disattivato o inattivo (ad esempio alla scadenza del periodo di prova Guest) sono conservati
            per un massimo di 24 mesi dall&apos;ultima interazione e poi cancellati;
          </li>
          <li>dati relativi alla Newsletter: fino alla revoca del consenso;</li>
          <li>
            log tecnici e di sicurezza: di regola fino a 6 mesi, salvo necessit&agrave; di ulteriore
            conservazione in caso di incidenti, abusi o richieste dell&apos;Autorit&agrave;;
          </li>
          <li>
            ulteriori conservazioni: nei limiti previsti dalla legge o necessari per
            l&apos;accertamento, l&apos;esercizio o la difesa di un diritto.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="10. Diritti dell'interessato">
        <p>
          L&apos;interessato pu&ograve; esercitare in qualsiasi momento i diritti di cui agli artt.
          15&ndash;22 GDPR: accesso, rettifica, cancellazione, limitazione del trattamento,
          portabilit&agrave;, opposizione al trattamento fondato sul legittimo interesse e revoca del
          consenso, senza pregiudicare la liceit&agrave; del trattamento svolto prima della revoca.
        </p>
        <p>
          Le richieste possono essere inviate a {BRAND.privacyEmail}. Il Titolare risponde entro un
          mese dalla richiesta.
        </p>
      </LegalSection>

      <LegalSection title="11. Decisioni automatizzate">
        <p>
          Il Titolare non adotta processi decisionali basati unicamente su trattamenti automatizzati
          che producano effetti giuridici o analogamente significativi sull&apos;interessato ai sensi
          dell&apos;art. 22 GDPR.
        </p>
      </LegalSection>

      <LegalSection title="12. Reclamo all'Autorità di controllo">
        <p>
          Se ritiene che il trattamento violi la normativa, l&apos;interessato pu&ograve; proporre
          reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it) o ricorrere
          all&apos;autorit&agrave; giudiziaria.
        </p>
      </LegalSection>

      <LegalSection title="13. Cookie e tecnologie similari">
        <p>
          La Piattaforma utilizza esclusivamente strumenti tecnici necessari al suo funzionamento e non
          utilizza cookie di profilazione, strumenti di analisi statistica o pixel pubblicitari. Il
          dettaglio &egrave; riportato nella <Link to="/cookie-policy" className="underline">Cookie Policy</Link>,
          che costituisce parte integrante della presente Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="14. Informativa ai sensi dell'art. 14 GDPR (dati non raccolti presso l'interessato)">
        <p>
          Per i dati personali contenuti negli avvisi di gara acquisiti da fonti pubbliche (punto 3.4)
          la presente sezione costituisce informativa ai sensi dell&apos;art. 14 GDPR. Gli interessati
          possono esercitare i diritti descritti al punto 10 scrivendo a {BRAND.privacyEmail}.
        </p>
      </LegalSection>

      <LegalSection title="15. Coerenza con Termini e Condizioni">
        <p>
          La presente Privacy Policy &egrave; coerente con i{' '}
          <Link to="/termini" className="underline">Termini e Condizioni</Link> di utilizzo della
          Piattaforma. In caso di contrasto prevalgono, per quanto riguarda il trattamento dei dati
          personali, le disposizioni della presente Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="16. Aggiornamenti">
        <p>
          La presente Privacy Policy pu&ograve; essere modificata o aggiornata, anche in conseguenza di
          variazioni normative o dell&apos;evoluzione dei servizi. La data dell&apos;ultima revisione
          &egrave; indicata in alto; le modifiche sostanziali saranno comunicate agli utenti registrati.
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPage;
